import { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { ImportLoraData } from '@/types/node';
import { useLoraView } from '../Recipe/FlowContext';
import LoRA from './MultiLoRA/LoRA';

interface ImportLoraPropertiesProps {
  id: string;
  data: ImportLoraData;
}

function ImportLoraProperties({ id, data }: ImportLoraPropertiesProps) {
  const { t } = useTranslation();
  const view = useLoraView(id);

  const { isLocked, loras } = data;

  const updateSelectedLora = useCallback(
    (newValue) => {
      const index = loras.findIndex((lora) => lora.id === newValue.id);
      if (index === -1) {
        return;
      }
      view.setSelectedIndex(index);
    },
    [view, loras],
  );

  const updateLora = useCallback(
    (lora) => {
      view.updateLora({
        ...lora,
        defaultWeight: typeof lora.defaultWeight === 'string' ? parseFloat(lora.defaultWeight) : lora.defaultWeight,
      });
    },
    [view],
  );

  const addLora = useCallback(() => {
    view.addLora();
  }, [view]);

  const deleteLora = useCallback(
    (loraId) => {
      view.deleteLora(loraId);
    },
    [view],
  );

  return (
    <>
      <Typography variant="body-sm-rg" sx={{ color: color.White64_T }}>
        {t(I18N_KEYS.RECIPE_MAIN.NODES.MULTI_LORA.LORAS)}
      </Typography>
      {loras.length > 0 &&
        loras.map((lora) => (
          <LoRA
            key={lora.id}
            lora={lora}
            updateLora={updateLora}
            deleteLora={deleteLora}
            setSelectedLora={updateSelectedLora}
            container="drawer"
          />
        ))}

      <Box sx={{ mt: 1 }}>
        <ButtonContained mode="text" size="small" onClick={addLora} sx={{ pointerEvents: isLocked ? 'none' : '' }}>
          {t(I18N_KEYS.RECIPE_MAIN.NODES.MULTI_LORA.ADD_LORA)}
        </ButtonContained>
      </Box>
    </>
  );
}

export default ImportLoraProperties;
